import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getSiteConfiguration,
  resetSiteConfiguration,
} from "store/slices/tool";
import { setLoading } from "store/slices/utils";
import UpdateIcon from "@mui/icons-material/Update";
import PersonIcon from "@mui/icons-material/Person";

function SiteConfiguration() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const { selectedSite } = useSelector((state) => state.site);
  const { siteConfiguration } = useSelector((state) => state.tool);

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [listedCategories, setListedCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [searchFilter, setSearchFilter] = useState("");
  const [custOwner, setCustOwner] = useState("ALL");

  // For Autocomplete
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategoryOption, setSelectedCategoryOption] = useState(null);

  // Update category options when listedCategories changes
  useEffect(() => {
    setCategoryOptions(
      listedCategories.map((cat) => ({ label: cat.category, ...cat })),
    );
  }, [listedCategories]);

  const handleCategorySelect = (viewedConfigIndex) => {
    const updatedCategories = listedCategories.map((item, index) => {
      if (index === viewedConfigIndex) {
        item.isSelected = true;
        setSelectedCategory(item);
      } else {
        item.isSelected = false;
      }
      return item;
    });
    setListedCategories(updatedCategories);
    setSelectedCategoryOption({
      label: updatedCategories[viewedConfigIndex].category,
      ...updatedCategories[viewedConfigIndex],
    });
  };

  // When Autocomplete changes
  const handleCategoryAutocomplete = (event, value) => {
    if (!value) return;
    const idx = listedCategories.findIndex(
      (cat) => cat.category === value.category,
    );
    if (idx !== -1) {
      handleCategorySelect(idx);
    }
  };

  const handleSearchFilter = () => {
    let filteredCategories = siteConfiguration.rows;
    if (searchFilter) {
      filteredCategories = filteredCategories.filter((item) => {
        return item.key.toLowerCase().includes(searchFilter.toLowerCase());
      });
    }
    const mappedCategories = mapListedCategories(filteredCategories);

    if (selectedCategory.category) {
      let isCategoryFound = false;
      for (let current of mappedCategories) {
        if (current.category === selectedCategory.category) {
          isCategoryFound = true;
          current.isSelected = true;
          setSelectedCategory(current);
          setSelectedCategoryOption({ label: current.category, ...current });
          break;
        }
      }
      if (!isCategoryFound) {
        setSelectedCategory({});
        setSelectedCategoryOption(null);
      }
    }
    setListedCategories(mappedCategories);
  };

  const getData = async () => {
    dispatch(setLoading(true));
    await dispatch(getSiteConfiguration({ selectedSite, custOwner }));
    dispatch(setLoading(false));
  };

  const sortProperty = (prop) => {
    return (a, b) => {
      if (a[prop] > b[prop]) {
        return 1;
      } else if (a[prop] < b[prop]) {
        return -1;
      } else {
        return 0;
      }
    };
  };

  const mapListedCategories = (data) => {
    const subCatMap = new Map();
    data.forEach((item) => {
      if (!subCatMap.has(item.category)) {
        subCatMap.set(item.category, []);
      }
      subCatMap.get(item.category).push(item);
    });

    const subCategories = Array.from(subCatMap, ([subCategory, settings]) => {
      return { subCategory, settings: settings.sort(sortProperty("key")) };
    });

    const catMap = new Map();
    subCategories.forEach((item) => {
      if (!catMap.has(item.subCategory.split(":")[0])) {
        catMap.set(item.subCategory.split(":")[0], []);
      }
      catMap.get(item.subCategory.split(":")[0]).push(item);
    });

    const categories = Array.from(catMap, ([category, subCategories]) => {
      return {
        category,
        subCategories: subCategories.sort(sortProperty("subCategory")),
        isSelected: false,
      };
    });

    categories.sort(sortProperty("category"));
    return categories;
  };

  const formatBooleanValue = (value) => {
    if (typeof value === "boolean") {
      return value ? "Enabled" : "Disabled";
    } else if (value === "Y" || value === "N") {
      return value === "Y" ? "Enabled" : "Disabled";
    } else {
      return value || "N/A";
    }
  };

  const getKeyBgColor = (index) => {
    if (theme.palette.mode === "dark") {
      if (index % 2 === 0) {
        return theme.palette.background.default;
      } else {
        return theme.palette.dark[800];
      }
    } else {
      if (index % 2 === 0) {
        return theme.palette.background.default;
      } else {
        return theme.palette.grey[100];
      }
    }
  };

  const isValueBoolean = (value) => {
    if (typeof value === "boolean") {
      return true;
    } else if (value === "Y" || value === "N") {
      return true;
    } else {
      return false;
    }
  };

  const isValueBooleanEnabled = (value) => {
    if (typeof value === "boolean") {
      return value;
    } else if (value === "Y" || value === "N") {
      return value === "Y";
    } else {
      return false;
    }
  };

  const applyValueStyles = (value) => {
    if (isValueBoolean(value)) {
      return {
        bgcolor: isValueBooleanEnabled(value)
          ? theme.palette.primary.main
          : theme.palette.mode === "dark"
            ? theme.palette.grey[300]
            : theme.palette.grey[200],
        color: isValueBooleanEnabled(value)
          ? "white"
          : theme.palette.text.primary,
      };
    } else {
      return {
        bgcolor: theme.palette.background.paper,
        color: theme.palette.primary.main,
        borderStyle: "solid",
        borderWidth: "2px",
        borderColor: theme.palette.primary.main,
      };
    }
  };

  useEffect(() => {
    getData();
    return () => {
      dispatch(resetSiteConfiguration());
    };
  }, [custOwner]);

  useEffect(() => {
    setListedCategories(mapListedCategories(siteConfiguration.rows));
  }, [siteConfiguration]);

  useEffect(() => {
    handleSearchFilter();
  }, [searchFilter]);

  useEffect(() => {
    if (isFirstLoad && listedCategories.length) {
      handleCategorySelect(0);
      setIsFirstLoad(false);
    }
  }, [listedCategories]);

  // Responsive layout using Grid
  return (
    <Box sx={{ width: "100%", p: isMdDown ? 1 : 2 }}>
      <Grid container spacing={isMdDown ? 2 : 3}>
        {/* Customer Owner and Category Autocomplete */}

        {/* Main Content */}
        <Grid size={{ xs: 12 }}>
          <Grid container spacing={isMdDown ? 2 : 3}>
            {/* Category List (hidden on mobile, shown on desktop) */}
            {!isMdDown && (
              <Grid size={{ xs: 12, sm: 5, md: 3 }}>
                <Stack spacing={isMdDown ? 1 : 2} mb={isMdDown ? 2 : 3}>
                  <Typography sx={{ fontWeight: "bold" }}>Customer Owner</Typography>
                  <Select
                    value={custOwner}
                    onChange={(e) => setCustOwner(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{
                      minWidth: "100%",
                      borderRadius: "4px",
                      mb: isMdDown ? 1 : 0,
                    }}
                  >
                    <MenuItem value="ALL">ALL</MenuItem>
                  </Select>
                  {isMdDown && (
                    <>
                      <Typography sx={{ fontWeight: "bold", mt: 1 }}>
                        Category
                      </Typography>
                      <Autocomplete
                        options={categoryOptions}
                        value={selectedCategoryOption}
                        onChange={handleCategoryAutocomplete}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.category === value.category
                        }
                        renderInput={(params) => (
                          <TextField {...params} size="small" fullWidth />
                        )}
                        sx={{ width: "100%" }}
                      />
                    </>
                  )}
                </Stack>
                <Box
                  sx={{
                    bgcolor: "background.paper",
                    padding: "10px",
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: theme.palette.mode === "light" 
                      ? theme.palette.grey[200] 
                      // : theme.palette.grey[300],
                      : "#6a6d8d",
                    display: "flex",
                    flexDirection: "column",
                    gap: "15px",
                  }}
                >
                  {/* Search input */}
                  <Box>
                    <TextField
                      placeholder="Search"
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Box>
                  {listedCategories.length ? (
                    <List
                      sx={{
                        padding: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      {listedCategories.map((item, index) => (
                        <ListItemButton
                          key={item.category}
                          selected={item.isSelected}
                          onClick={() => handleCategorySelect(index)}
                          sx={{
                            borderRadius: "4px",
                            marginRight: "2px",
                          }}
                        >
                          <ListItemText
                            primary={item.category}
                            sx={{
                              ".MuiTypography-root": {
                                fontWeight: item.isSelected ? "bold" : "normal",
                              },
                              ".MuiListItemText-primary": {
                                color: item.isSelected
                                  ? theme.palette.primary.main
                                  : theme.palette.text.primary,
                              },
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  ) : null}
                </Box>
              </Grid>
            )}

            {/* Details Panel */}
            <Grid size={{ xs: 12, sm: 12, md: isMdDown ? 12 : 9 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  width: "100%",
                  padding: isMdDown ? "0" : "15px",
                }}
              >
                {selectedCategory?.subCategories?.map((item) => (
                  <Box key={item.subCategory}>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "18px",
                        paddingBottom: "10px",
                      }}
                    >
                      {item.subCategory}
                    </Typography>
                    <List sx={{ padding: "10px 0" }}>
                      {item.settings.map((subItem, subIndex) => (
                        <ListItem
                          key={subItem.key}
                          gap="10px"
                          sx={{
                            padding: "15px",
                            bgcolor: getKeyBgColor(subIndex),
                            borderWidth:
                              subIndex === 0 ? "1px" : "0 1px 1px 1px",
                            borderColor: theme.palette.mode === "light" 
                              ? theme.palette.grey[200] 
                              // : theme.palette.grey[300],
                              : "#6a6d8d",
                            borderStyle: "solid",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "15px",
                            flexWrap: "wrap",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "10px",
                              flex: "1 1 260px",
                              minWidth: isMdDown ? "180px" : "460px",
                            }}
                          >
                            <Box sx={{ fontWeight: "bold", fontSize: "16px" }}>
                              {subItem.key}
                            </Box>
                            <Box
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: "14px",
                                fontStyle: "italic"
                              }}
                            >
                              {subItem.description}
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: "15px",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              flexWrap: "wrap",
                              minWidth: isMdDown ? "180px" : "280px",
                              flex: "1 1 180px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "5px",
                                flexShrink: "0",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <UpdateIcon
                                  sx={{
                                    fontSize: "20px",
                                    color: theme.palette.grey[500],
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: "12px !important",
                                    fontWeight: "bold",
                                    color: theme.palette.grey[500],
                                  }}
                                >
                                  Updated
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "12px !important" }}
                                >
                                  {subItem?.changedAt
                                    ? new Date(
                                        subItem.changedAt,
                                      ).toLocaleString("en-US", {
                                        month: "numeric",
                                        day: "numeric",
                                        year: "2-digit",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                      })
                                    : "N/A"}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <PersonIcon
                                  sx={{
                                    fontSize: "20px",
                                    color: theme.palette.grey[500],
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: "12px !important",
                                    fontWeight: "bold",
                                    color: theme.palette.grey[500],
                                  }}
                                >
                                  User
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "12px !important" }}
                                >
                                  {subItem?.changedBy ?? "N/A"}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                padding: "10px",
                                borderColor: theme.palette.primary.main,
                                fontSize: "16px",
                                borderRadius: "100px",
                                ".MuiTypography-root": { fontWeight: "500" },
                                ...applyValueStyles(subItem.value),
                              }}
                            >
                              <Typography>
                                {formatBooleanValue(subItem.value)}
                              </Typography>
                            </Box>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default SiteConfiguration;
